// AGENT APPROVALS — what the bot wants to do, why, and the exact content.
//
// Requirement 6. An ACTION tool never executes; it files one of these, and a
// person reads it and presses Approve, Edit or Reject.
//
// ── IT EXTENDS THE EXISTING QUEUE, IT DOES NOT ADD A SECOND ONE ────────────
// `approvals` already exists: chief_bot files scrub_batch / run_campaign /
// ditch_batch / lead_chain requests, /approvals reviews them, and
// approveAndDispatch executes them with a per-run dollar budget. Four statuses
// and a 48-hour expiry are already proven there. Adding a parallel collection
// for agent approvals would mean two queues, two expiry rules and two places
// to look. So these are the same collection with agent types added, and the
// four existing types are untouched.
//
// ── WHAT AN APPROVAL MUST CARRY ────────────────────────────────────────────
// The owner's wording: "what the bot wants to do, why, exact content". A
// request that cannot show the exact content cannot be approved — approving a
// summary is approving a thing you have not read. validateAgentApproval
// refuses one with no summary, no reason, or no content.
//
// Pure: no React, no Firestore.

// Agent request types. Each names a REGISTERED ACTION tool, so a type that no
// tool can produce cannot be filed, and a tool whose type is not here cannot
// reach the queue.
export const AGENT_APPROVAL_TYPES = {
  noc_send: {
    // ⚠️ THE NAME OF THE DOCUMENT. An NOC is a NOTICE OF COMMENCEMENT. A
    // "Notice to Owner" is a DIFFERENT Florida document — served by a supplier
    // or a subcontractor on the property owner to preserve a lien right — and
    // RoofBeam has its own NTO template and its own checklist task for it.
    // This label said "Notice to Owner" from the day it was written, and the
    // Permit Bot's first real report dutifully repeated it. The model was not
    // hallucinating: it was quoting us. See permitFacts.js BANNED_TERMS, which
    // now refuses the wrong word in agent output.
    key: 'noc_send', tool: 'noc.send', label: 'Send the Notice of Commencement',
    // What a person is actually authorising. Written in the first person
    // because it is read on a phone next to an Approve button.
    consequence: 'The NOC document goes to the homeowner through the signing flow. This affects lien rights — read the document before approving.',
    needs: ['jobId', 'summary', 'why'],
  },
  permit_status_change: {
    key: 'permit_status_change', tool: 'permit.statusChange', label: 'Change a permit status',
    consequence: "The permit's status changes on the job, which moves the production gate — an 'issued' permit lets a crew start.",
    needs: ['jobId', 'summary', 'why', 'toStatus'],
  },
};

export const AGENT_APPROVAL_KEYS = Object.keys(AGENT_APPROVAL_TYPES);

// The four types that were already in the collection before agents existed.
// Listed so a reader of either queue can tell at a glance which is which, and
// so a test can assert nothing here collides with them.
export const LEGACY_APPROVAL_TYPES = ['scrub_batch', 'run_campaign', 'ditch_batch', 'lead_chain'];

export function isAgentApproval(docArg) {
  const d = docArg || {};
  return AGENT_APPROVAL_KEYS.includes(String(d.type || ''));
}

export function approvalTypeMeta(typeArg) {
  return AGENT_APPROVAL_TYPES[String(typeArg || '')] || null;
}

// Mirrors approveAndDispatch's own 48-hour rule. A stale request is a request
// about a state of the world that has moved on.
export const APPROVAL_EXPIRY_MS = 48 * 60 * 60 * 1000;

export function approvalIsExpired(docArg, nowArg) {
  const d = docArg || {};
  if (d.status !== 'pending') return false;
  const ms = stampMs(d.createdAt);
  if (!ms) return false;
  return (Number(nowArg) || Date.now()) - ms > APPROVAL_EXPIRY_MS;
}

function stampMs(v) {
  if (v == null) return 0;
  if (typeof v === 'number') return v;
  if (typeof v === 'string') { const n = Date.parse(v); return Number.isFinite(n) ? n : 0; }
  if (typeof v.toMillis === 'function') { try { return v.toMillis(); } catch { return 0; } }
  if (typeof v.getTime === 'function') { try { return v.getTime(); } catch { return 0; } }
  if (typeof v.seconds === 'number') return v.seconds * 1000;
  return 0;
}

// ── FILING ONE ──────────────────────────────────────────────────────────────
// args.tool      the ACTION tool that produced it
// args.proposed  that tool's describe output: { summary, why, jobId, ... }
// args.ctx       { companyId, agentKey, runId }
//
// Returns { ok, doc, error }. status is ALWAYS 'pending' — there is no path
// through this function that files something already approved.
export function buildAgentApproval(argsArg) {
  const a = argsArg || {};
  const toolName = String(a.tool || '');
  const type = AGENT_APPROVAL_KEYS.find(k => AGENT_APPROVAL_TYPES[k].tool === toolName);
  if (!type) {
    return { ok: false, error: `"${toolName}" is not an action that can be approved. Add it to AGENT_APPROVAL_TYPES first.` };
  }
  const meta = AGENT_APPROVAL_TYPES[type];
  const proposed = a.proposed && typeof a.proposed === 'object' ? a.proposed : {};
  const ctx = a.ctx && typeof a.ctx === 'object' ? a.ctx : {};

  const summary = String(proposed.summary || '').trim();
  const why = String(proposed.why || '').trim();
  if (!summary) return { ok: false, error: 'An approval has to say what it wants to do.' };
  if (!why) return { ok: false, error: 'An approval has to say why. A request with no reason is not reviewable.' };
  if (!ctx.companyId) return { ok: false, error: 'An approval has to carry a companyId.' };
  if (!ctx.agentKey) return { ok: false, error: 'An approval has to name the agent that filed it.' };
  for (const f of meta.needs) {
    if (f === 'summary' || f === 'why') continue;
    if (proposed[f] === undefined || proposed[f] === null || proposed[f] === '') {
      return { ok: false, error: `A ${meta.label} request needs ${f}.` };
    }
  }

  return {
    ok: true,
    doc: {
      type, status: 'pending',
      // Requirement 1 — company-scoped from day one.
      companyId: String(ctx.companyId),
      agentKey: String(ctx.agentKey),
      runId: ctx.runId ? String(ctx.runId) : null,
      tool: toolName,
      jobId: proposed.jobId ? String(proposed.jobId) : null,
      // The three things a person needs: what, why, and the exact content.
      summary, why,
      consequence: meta.consequence,
      payload: { ...proposed },
      requestedBy: `agent:${ctx.agentKey}`,
      decidedBy: null, decidedAt: null, decisionNote: null,
      // Edits the reviewer made before approving. Never applied silently —
      // the executor reads `edited` when present and `payload` otherwise, and
      // the record keeps both so what was approved is auditable.
      edited: null,
    },
  };
}

// ── DECIDING ONE ────────────────────────────────────────────────────────────
// Approve / Edit-then-approve / Reject. Returns the dotted patch to write, so
// one decision cannot clobber a field another session just wrote.
//
// THE REVIEWER IS ALWAYS NAMED. A decision with no `by` is refused — an
// approval history that cannot say who approved it is not a history.
export const APPROVAL_DECISIONS = ['approve', 'reject'];

export function decideApproval(argsArg) {
  const a = argsArg || {};
  const doc = a.approval && typeof a.approval === 'object' ? a.approval : null;
  const decision = String(a.decision || '');
  const by = String(a.by || '').trim();
  const now = a.at || new Date().toISOString();

  if (!doc) return { ok: false, error: 'No approval to decide.' };
  if (!APPROVAL_DECISIONS.includes(decision)) return { ok: false, error: `"${decision}" is not a decision.` };
  if (!by) return { ok: false, error: 'A decision has to record who made it.' };
  if (doc.status !== 'pending') {
    return { ok: false, error: `This request is already ${doc.status}. It cannot be decided again.` };
  }
  if (approvalIsExpired(doc, a.now)) {
    // Expired is a real outcome, not an error the reviewer caused — the patch
    // records it so the queue stops showing it as actionable.
    return {
      ok: false, expired: true,
      error: 'This request is more than 48 hours old. The job may have moved on — run the agent again rather than approving a stale request.',
      patch: { status: 'expired', decidedAt: now },
    };
  }

  const patch = {
    status: decision === 'approve' ? 'approved' : 'denied',
    decidedBy: by, decidedAt: now,
    decisionNote: a.note ? String(a.note) : null,
  };
  // An EDIT is recorded separately from the original, so the audit trail shows
  // both what the agent asked for and what the person actually authorised.
  if (decision === 'approve' && a.edited && typeof a.edited === 'object' && Object.keys(a.edited).length) {
    patch.edited = { ...a.edited, editedBy: by, editedAt: now };
  }
  return { ok: true, patch, executes: decision === 'approve' };
}

// What the executor should act on: the reviewer's edit when there was one,
// otherwise exactly what the agent proposed.
export function effectivePayload(docArg) {
  const d = docArg || {};
  const base = d.payload && typeof d.payload === 'object' ? d.payload : {};
  const edited = d.edited && typeof d.edited === 'object' ? d.edited : null;
  if (!edited) return { ...base, wasEdited: false };
  const { editedBy, editedAt, ...fields } = edited;
  return { ...base, ...fields, wasEdited: true, editedBy: editedBy || null, editedAt: editedAt || null };
}

// ── THE COMMAND CENTER ITEM ─────────────────────────────────────────────────
// One pending approval, in the shape the Command Center's unified list already
// uses for a system alert ({ kind, id, severity, jobId, ... }) so it sorts and
// filters alongside everything else rather than living in its own box.
export function approvalAsCommandCenterItem(docArg, optsArg) {
  const d = docArg || {};
  const o = optsArg || {};
  const meta = approvalTypeMeta(d.type);
  if (!meta || d.status !== 'pending') return null;
  const expired = approvalIsExpired(d, o.now);
  return {
    kind: 'approval',
    id: `approval:${d.id || ''}`,
    approvalId: d.id || null,
    jobId: d.jobId || null,
    // High: a person is BLOCKING the agent's work until they look. An expired
    // one drops to med — it is no longer actionable, only tidy-up.
    severity: expired ? 'med' : 'high',
    type: d.type,
    title: `${o.agentName || d.agentKey || 'Agent'} wants to ${meta.label.toLowerCase()}`,
    detail: d.summary || '',
    why: d.why || '',
    consequence: meta.consequence,
    expired,
    // Routed to the owner by default: these are decisions, and the owner's
    // ruling put the OS in front of the owner and the sales manager.
    assigneeUid: o.ownerUid || null,
    assigneeName: o.ownerName || 'Owner',
  };
}

export function pendingApprovalSummary(docsArg, optsArg) {
  const list = (Array.isArray(docsArg) ? docsArg : []).filter(d => d && d.status === 'pending' && isAgentApproval(d));
  const o = optsArg || {};
  const live = list.filter(d => !approvalIsExpired(d, o.now));
  const stale = list.length - live.length;
  if (!list.length) return '';
  return `${live.length} waiting on you`
    + (stale ? ` · ${stale} expired` : '');
}
